import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { ActionsForLegalSupport } from './ActionsForLegalSupport';
import { ListOfRisks } from './ListOfRisks';
import { actionsForSupportValue, LEGAL_FRAMEWORK_AND_RISKS_NAMES, listOfRisksValue, prefix } from './utils/form';

export const LegalFrameworkAndRisks: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS);

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('legalFrameworkAndRisks.title')}
          section={FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS}
        />
      </FormSectionCard>
      {/* 10.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('legalFrameworkAndRisks.generalLaws')}
        aiFieldName={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.GENERAL_LAWS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.GENERAL_LAWS)}
          name={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.GENERAL_LAWS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 10.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('legalFrameworkAndRisks.industryRequirements')}
        aiFieldName={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.INDUSTRY_REQUIREMENTS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.INDUSTRY_REQUIREMENTS)}
          name={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.INDUSTRY_REQUIREMENTS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 10.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('legalFrameworkAndRisks.missingRequirements')}
        aiFieldName={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LEGAL_REQUIREMENTS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LEGAL_REQUIREMENTS)}
          name={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LEGAL_REQUIREMENTS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 10.4 */}
      <FormSectionCard collapsible disabled={!canAccess} title={t('legalFrameworkAndRisks.listOfActions.title')}>
        <FieldArrayComponent
          name={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LIST_OF_ACTIONS)}
          initialItem={actionsForSupportValue}
          buttonText={t('legalFrameworkAndRisks.listOfActions.action')}
          disabled={!canAccess}
          maxLength={10}
        >
          {(namePrefix) => <ActionsForLegalSupport name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 10.5 */}
      <FormSectionCard
        collapsible
        disabled={!canAccess}
        title={t('legalFrameworkAndRisks.listOfRisks.title')}
        aiFieldName={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LIST_OF_RISKS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, LEGAL_FRAMEWORK_AND_RISKS_NAMES.LIST_OF_RISKS)}
          initialItem={listOfRisksValue}
          buttonText={t('legalFrameworkAndRisks.listOfRisks.buttonTitle')}
          disabled={!canAccess}
          maxLength={15}
        >
          {(namePrefix) => <ListOfRisks name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
